import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getVendorCountByCategory } from 'utils/apiRequests';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { colors } from '@alixpartners/ui-components';
import Highcharts from 'highcharts';
import {
	APCategoryMapper,
	ClientCategoryMapper,
	getRequiredCustomFilterObjectFormat,
	TAXONOMY_TYPES,
	UNITS,
} from '../../CategoryManagement/utils';

const SupplierLandscape = createContext();
// vendorCountBySpend = bar
// vendorCount = line

export const SupplierLandscapeProvider = ({ children }) => {
	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [unit, setUnit] = useState('m');
	const [value, setValue] = useState(1);
	const [category, setCategory] = useState('AP_L4Category');
	const [options, setOptions] = useState({
		chart: {
			zoomType: 'xy',
			height: chartheight,
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		xAxis: {
			// categories: categoriesList,
			crosshair: true,
			lineColor: colors.black,
			labels: {
				formatter: function() {
					if (typeof this.value !== 'number') {
						if (this.value === undefined) {
							return '';
						} else {
							if (this.value.length > 0) {
								return `${this.value.substring(0, 4)}...`;
							}
						}
					}
				},
				style: { ...FONTSTYLE },
			},
		},
		yAxis: [
			{
				gridLineWidth: 0,
				lineColor: colors.black,
				lineWidth: 1,
				// labels: { enabled: false },
				title: {
					text: 'Total Category Vendors',
					style: {
						...FONTSTYLE,
						textOverflow: 'ellipsis',
					},
				},
			},
			{
				gridLineWidth: 0,
				// labels: { enabled: false },
				lineColor: colors.black,
				lineWidth: 1,
				title: {
					text: 'Vendor Count > Threshold',
					style: {
						...FONTSTYLE,
						fontSize: '9px',
						textOverflow: 'ellipsis',
					},
				},
				opposite: true,
			},
		],
		tooltip: { shared: true },
		legend: {
			align: 'center',
			verticalAlign: 'top',
			x: 0,
			y: -18,
			floating: true,
			backgroundColor:
				Highcharts.defaultOptions.legend.backgroundColor || // theme
				'rgba(255,255,255,0.25)',
			itemStyle: {
				...FONTSTYLE,
				fontSize: '9px',
				textOverflow: 'ellipsis',
			},
		},
		// series: [],
		colors: ['#032E45', '#448EB4'],
	});

	useEffect(
		() => {
			taxonomyType === TAXONOMY_TYPES.CLIENT
				? setCategory(APCategoryMapper.L4)
				: setCategory(ClientCategoryMapper.L4);
		},
		[taxonomyType]
	);

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						spend: value * UNITS.find(item => item.key === unit).worth,
						selectCategoryLevel: category,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getVendorCountByCategory(payload)
						.then(response => {
							if (response.length > 0) {
								setData(response);
								const categoriesResult = response.map(obj => obj.category);
								const vendorCountBySpendBar = response.map(
									obj => obj.vendorCountBySpend
								);
								const vendorCountLine = response.map(obj => obj.vendorCount);
								const seriesResult = [
									{
										name: 'Vendor Count > Threshold',
										type: 'column',
										yAxis: 1,
										data: vendorCountBySpendBar,
									},
									{
										name: 'Total Category Vendors',
										type: 'spline',
										data: vendorCountLine,
									},
								];
								setOptions(prevState => ({
									...prevState,
									xAxis: {
										...prevState.xAxis,
										categories: categoriesResult,
									},
									series: seriesResult,
								}));
							}
						})
						.catch(() => setIsLoading(false))
						.finally(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			unit,
			value,
			category,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	const changeCategory = e => {
		if (e.target?.name === 'categoryName') {
			taxonomyType === TAXONOMY_TYPES.CLIENT
				? setCategory(APCategoryMapper[e.target.value])
				: setCategory(ClientCategoryMapper[e.target.value]);
		} else if (e.target?.name === 'categoryUnit') {
			setUnit(UNITS.find(item => item.value === e.target.value).key);
		} else {
			setValue(e);
		}
	};

	return (
		<SupplierLandscape.Provider
			value={{
				isLoading,
				data,
				options,
				unit,
				value,
				category,
				UNITS,
				changeCategory,
			}}
		>
			{children}
		</SupplierLandscape.Provider>
	);
};

export const useSupplierLandscapeContext = () => {
	return useContext(SupplierLandscape);
};
